import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, X } from 'lucide-react';
import ActionButton from './UI/ActionButton';
import DocumentTile, { DOCUMENT_GRID } from './UI/DocumentTile';
import { fetchDocumentBlobUrl, openDocument, downloadDocument } from '../utils/documentLinks';

const isImageName = (name) => /\.(png|jpe?g|gif|bmp|webp)$/i.test(String(name || ''));

/**
 * A row of documents with an in-page viewer underneath.
 *
 * View never opens a tab. The API sends X-Frame-Options: SAMEORIGIN, so a frame
 * pointed at a signed /view URL is blocked whenever the API is on a different
 * origin than the app; the bytes are fetched and shown from a blob: URL, which
 * belongs to the page and always renders. Opening in a tab is still offered,
 * but as a choice rather than the only way — a blocked pop-up used to send the
 * whole app to the file and lose the page.
 *
 * @param {Array}    documents    rows with id, document_type, file_path
 * @param {boolean}  loading
 * @param {string}   error
 * @param {string}   emptyMessage shown when there are no documents
 * @param {Function} badgeFor     optional (doc) => string | {label, tone}, a tag
 *                                on the tile. tone 'muted' plays it down.
 * @param {Element}  viewerTarget optional element to show the viewer in (e.g. a
 *                                full-width row), instead of under the tiles.
 */
const DocumentsStrip = ({
  documents = [],
  loading = false,
  error = '',
  emptyMessage = 'No documents on file.',
  badgeFor = null,
  className = '',
  viewerTarget = null,
}) => {
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewError, setPreviewError] = useState('');
  // The object URL on screen, and the document last asked for, so a quick
  // second click cannot leak the first one or land out of order.
  const previewUrlRef = useRef(null);
  const requestedDocRef = useRef(null);

  const releasePreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  const closePreview = () => {
    requestedDocRef.current = null;
    releasePreviewUrl();
    setPreviewDoc(null);
    setPreviewError('');
  };

  // Shown elsewhere on the page: bring it into view when a document opens.
  useEffect(() => {
    if (viewerTarget && previewDoc?.id) {
      viewerTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [viewerTarget, previewDoc?.id]);

  // Let go of the object URL when this unmounts.
  useEffect(() => releasePreviewUrl, []);

  // Nothing should stay open for a document that is no longer listed.
  useEffect(() => {
    if (previewDoc && !documents.some((d) => d.id === previewDoc.id)) closePreview();
    // eslint-disable-next-line
  }, [documents]);

  const togglePreview = async (doc) => {
    if (previewDoc?.id === doc.id) {
      closePreview();
      return;
    }
    requestedDocRef.current = doc.id;
    releasePreviewUrl();
    setPreviewError('');
    setPreviewDoc({ id: doc.id, document_type: doc.document_type, file_path: doc.file_path, url: null });
    try {
      const url = await fetchDocumentBlobUrl(doc.id);
      if (requestedDocRef.current !== doc.id) {
        URL.revokeObjectURL(url);
        return;
      }
      releasePreviewUrl();
      previewUrlRef.current = url;
      setPreviewDoc((prev) => (prev && prev.id === doc.id ? { ...prev, url } : prev));
    } catch (err) {
      if (requestedDocRef.current !== doc.id) return;
      setPreviewDoc(null);
      setPreviewError(err?.response?.data?.error || err?.message || 'Could not open this document.');
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center gap-2 py-6 ${className}`}>
        <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        <span className="text-xs text-gray-500 dark:text-slate-400">Loading documents…</span>
      </div>
    );
  }
  if (error) {
    return <p className={`text-sm text-red-600 dark:text-red-400 py-3 ${className}`}>{error}</p>;
  }
  if (!documents.length) {
    return <p className={`py-6 text-center text-sm text-gray-400 dark:text-slate-500 ${className}`}>{emptyMessage}</p>;
  }

  const viewer = previewDoc ? (
        <div className="mt-3 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 dark:bg-slate-800/60 border-b border-gray-200 dark:border-slate-700 flex flex-wrap items-center gap-2">
            <FileText size={14} className="flex-shrink-0 text-blue-600 dark:text-blue-400" />
            <span className="text-xs font-semibold text-gray-800 dark:text-slate-100 truncate">
              {previewDoc.document_type || 'Document'}
            </span>
            <span className="text-[11px] text-gray-400 dark:text-slate-500 truncate min-w-0">
              {previewDoc.file_path || ''}
            </span>
            <div className="ml-auto flex items-center gap-1">
              <ActionButton
                type="button"
                onClick={async () => {
                  try {
                    setPreviewError('');
                    await openDocument(previewDoc.id);
                  } catch (err) {
                    setPreviewError(err?.message || 'Could not open this document in a new tab.');
                  }
                }}
                className="px-2 py-1 rounded text-[11px] font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700"
                busyLabel="…"
              >
                Open in new tab
              </ActionButton>
              <ActionButton
                type="button"
                onClick={() => downloadDocument(previewDoc.id)}
                className="px-2 py-1 rounded text-[11px] font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700"
                busyLabel="…"
              >
                Download
              </ActionButton>
              <button
                type="button"
                onClick={closePreview}
                aria-label="Close preview"
                className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-200 dark:hover:bg-slate-700"
              >
                <X size={14} />
              </button>
            </div>
          </div>
          {!previewDoc.url ? (
            <div className="flex items-center justify-center gap-2 py-16 bg-white dark:bg-slate-900">
              <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              <span className="text-xs text-gray-500 dark:text-slate-400">Opening…</span>
            </div>
          ) : isImageName(previewDoc.file_path) ? (
            <div className="bg-white dark:bg-slate-900 p-3 max-h-[70vh] overflow-auto">
              <img src={previewDoc.url} alt={previewDoc.document_type || 'Document'} className="max-w-full h-auto mx-auto rounded" />
            </div>
          ) : (
            <iframe
              title={previewDoc.document_type || 'Document'}
              src={previewDoc.url}
              className="w-full h-[70vh] bg-white dark:bg-slate-900"
            />
          )}
        </div>
  ) : null;

  return (
    <div className={className}>
      {previewDoc && viewerTarget && createPortal(viewer, viewerTarget)}
      {/* The shared document tiles; View shows the document on the page. */}
      <div className={DOCUMENT_GRID}>
        {documents.map((doc) => {
          const badge = typeof badgeFor === 'function' ? badgeFor(doc) : null;
          return (
            <DocumentTile
              key={doc.id}
              type={doc.document_type || 'Document'}
              doc={doc}
              badge={badge && typeof badge === 'object' ? badge.label : badge}
              viewing={previewDoc?.id === doc.id}
              onView={() => togglePreview(doc)}
            />
          );
        })}
      </div>

      {previewError && (
        <p className="mt-3 p-2 rounded-lg text-sm bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300">
          {previewError}
        </p>
      )}

      {previewDoc && !viewerTarget && viewer}
    </div>
  );
};

export default DocumentsStrip;